import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, Trash2, ArrowLeft } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { Logo } from "../components/site/Logo";

const STATUSES = ["pending", "confirmed", "completed", "cancelled"];
const STATUS_COLOR = { pending: "var(--sun)", confirmed: "var(--sky)", completed: "var(--grass)", cancelled: "#E5E7EB" };

const Login = ({ onLogin }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    setError("");
    try {
      const { data } = await api.post("/auth/login", { email, password });
      sessionStorage.setItem("ypp_token", data.token);
      onLogin(data);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    }
  };
  return (
    <form onSubmit={submit} data-testid="admin-login-form" className="sticker rounded-[28px] bg-white p-8 max-w-md mx-auto space-y-5">
      <h1 className="font-display font-bold text-4xl">Parent login</h1>
      <input required type="email" placeholder="Email" data-testid="admin-email-input" className="field" value={email} onChange={(e) => setEmail(e.target.value)} />
      <input required type="password" placeholder="Password" data-testid="admin-password-input" className="field" value={password} onChange={(e) => setPassword(e.target.value)} />
      {error && <p data-testid="admin-login-error" className="text-sm font-semibold text-red-600">{error}</p>}
      <button type="submit" data-testid="admin-login-btn" className="w-full sticker-sm rounded-full bg-[var(--coral)] text-white font-display font-semibold text-lg py-3">
        Log in
      </button>
    </form>
  );
};

const BookingCard = ({ b, onStatus, onDelete }) => (
  <div data-testid={`booking-card-${b.id}`} className="sticker-sm rounded-2xl bg-white p-5 grid md:grid-cols-12 gap-4 items-start">
    <div className="md:col-span-4">
      <p className="font-display font-semibold text-xl">{b.name}</p>
      <p className="text-sm text-[var(--ink-soft)] break-all">{b.email} · {b.phone}</p>
      <p className="text-sm text-[var(--ink-soft)] mt-1">{b.address}</p>
    </div>
    <div className="md:col-span-5 text-sm space-y-1">
      <p><b>{b.occasion}</b> · {b.package} · <b>{b.date}</b></p>
      {b.honoree && <p>For: {b.honoree}</p>}
      {b.message && <p className="text-[var(--ink-soft)]">"{b.message}"</p>}
    </div>
    <div className="md:col-span-3 flex items-center gap-2 md:justify-end">
      <select
        data-testid={`booking-status-${b.id}`}
        value={b.status}
        onChange={(e) => onStatus(b.id, e.target.value)}
        className="rounded-full border-[2.5px] border-[var(--ink)] px-3 py-1.5 font-semibold text-sm capitalize"
        style={{ background: STATUS_COLOR[b.status] }}
      >
        {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
      </select>
      <button data-testid={`booking-delete-${b.id}`} onClick={() => onDelete(b.id)} aria-label="Delete" className="p-2 rounded-full hover:bg-red-100">
        <Trash2 size={18} />
      </button>
    </div>
  </div>
);

export default function Admin() {
  const [user, setUser] = useState(null);
  const [checked, setChecked] = useState(false);
  const [bookings, setBookings] = useState([]);
  const [filter, setFilter] = useState("all");

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/bookings");
      setBookings(Array.isArray(data) ? data : []);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  }, []);

  useEffect(() => {
    api.get("/auth/me").then((r) => setUser(r.data)).catch(() => setUser(null)).finally(() => setChecked(true));
  }, []);

  useEffect(() => {
    if (user) load();
  }, [user, load]);

  const onStatus = async (id, status) => {
    const { data } = await api.patch(`/bookings/${id}`, { status });
    setBookings((bs) => bs.map((b) => (b.id === id ? data : b)));
  };
  const onDelete = async (id) => {
    if (!window.confirm("Delete this request?")) return;
    await api.delete(`/bookings/${id}`);
    setBookings((bs) => bs.filter((b) => b.id !== id));
  };
  const logout = async () => {
    await api.post("/auth/logout");
    sessionStorage.removeItem("ypp_token");
    setUser(null);
  };

  const shown = filter === "all" ? bookings : bookings.filter((b) => b.status === filter);

  return (
    <div data-testid="admin-page" className="min-h-screen px-4 sm:px-8 py-8 max-w-6xl mx-auto">
      <div className="flex items-center justify-between mb-12">
        <Link to="/" data-testid="admin-home-link" className="flex items-center gap-3"><ArrowLeft size={18} /><Logo /></Link>
        {user && (
          <button data-testid="admin-logout-btn" onClick={logout} className="sticker-sm rounded-full bg-white px-4 py-2 flex items-center gap-2 font-semibold text-sm">
            <LogOut size={16} /> Log out
          </button>
        )}
      </div>
      {!checked ? null : !user ? (
        <Login onLogin={setUser} />
      ) : (
        <>
          <h1 className="font-display font-bold text-4xl sm:text-5xl">Booking requests</h1>
          <div className="flex flex-wrap gap-2 mt-6 mb-8">
            {["all", ...STATUSES].map((s) => (
              <button
                key={s}
                data-testid={`admin-filter-${s}`}
                onClick={() => setFilter(s)}
                className={`sticker-sm rounded-full px-4 py-1.5 text-sm font-semibold capitalize ${filter === s ? "bg-[var(--ink)] text-white" : "bg-white"}`}
              >
                {s} ({s === "all" ? bookings.length : bookings.filter((b) => b.status === s).length})
              </button>
            ))}
          </div>
          <div className="space-y-4" data-testid="admin-booking-list">
            {shown.length === 0 && <p data-testid="admin-empty" className="text-[var(--ink-soft)]">No requests yet.</p>}
            {shown.map((b) => <BookingCard key={b.id} b={b} onStatus={onStatus} onDelete={onDelete} />)}
          </div>
        </>
      )}
    </div>
  );
}
